import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import { InventoryFormatTypeOption } from '@openx/types/targeting/formatType.ts';
import {
  type DisplayContextsType,
  displayContexts,
  type TargetingCustomOptions,
} from '@openx/types/targeting/targeting.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingOptionDisabledState } from '../../../utils/hooks/useTargetingOptionDisabledState.ts';

export type InventoryContentOptionDropdownProps = {
  onChange: (type: InventoryFormatTypeOption) => void;
  value: InventoryFormatTypeOption;
  customOptions?: TargetingCustomOptions<InventoryFormatTypeOption>;
  displayContext?: DisplayContextsType;
};

export const FormatTypeGroupOptionDropdown = (props: InventoryContentOptionDropdownProps) => {
  const { onChange, value, customOptions, displayContext } = props;
  const { t } = useTranslation();
  const { isOptionDisabled } = useTargetingOptionDisabledState();

  const allLabels: Record<InventoryFormatTypeOption, string> = useMemo(
    () => ({
      [InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]:
        displayContext === displayContexts.TRAFFIC_SET ? t('Distribution Channel') : t('Channel'),
      [InventoryFormatTypeOption.AD_PLACEMENT]:
        displayContext === displayContexts.TRAFFIC_SET ? t('Ad Placement') : t('Format'),
    }),
    [displayContext, t]
  );

  const availableLabels = useMemo(() => {
    if (!customOptions?.availableOptions) {
      return allLabels;
    }

    const filtered: Partial<Record<InventoryFormatTypeOption, string>> = {};

    customOptions.availableOptions.forEach(option => {
      filtered[option] = allLabels[option];
    });

    return filtered;
  }, [customOptions, allLabels]);

  return (
    <AutoComplete
      textFieldProps={{ label: t('Type') }}
      options={Object.keys(availableLabels)}
      renderOptions={{
        dataTest: 'type-option',
      }}
      getOptionLabel={option => availableLabels[option] || option}
      getOptionDisabled={option => isOptionDisabled(option)}
      textFieldReadOnly
      disableClearable
      onChange={(_, value) => onChange(value as InventoryFormatTypeOption)}
      value={value}
      sx={{
        marginTop: '4px',
      }}
      data-test="format-type-category-type"
    />
  );
};
